import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { config } from '../config';

export interface OAuthProvider {
  id: string;
  /** Stable key (e.g. "google") used to choose a branded button. */
  slug: string;
  displayName: string;
}

export interface OAuthProvidersScope {
  /** Also surface the inviting org's providers (invitation-acceptance flow). */
  invitationToken?: string;
  /**
   * Also surface this org's own providers. Set it when the caller already knows
   * who is signing in — re-authenticating an expired session — since an
   * org-scoped provider is otherwise invisible and an SSO-only user has no
   * password to fall back on.
   */
  organizationId?: string | null;
}

/**
 * Enabled OAuth/OIDC providers offered in the current context. Global providers
 * are always returned; `invitationToken` and `organizationId` each widen the set
 * with that org's providers. Public endpoint — no auth required.
 *
 * Both scoping keys are part of the query key: the same call with a different
 * scope is a different result set, and sharing one cache entry would let a
 * narrower earlier fetch mask the org's own providers.
 */
export function useOAuthProviders({ invitationToken, organizationId }: OAuthProvidersScope = {}) {
  return useQuery({
    queryKey: ['oauthProviders', invitationToken ?? null, organizationId ?? null],
    queryFn: async (): Promise<OAuthProvider[]> => {
      const params: Record<string, string> = {};
      if (invitationToken) params.invitationToken = invitationToken;
      if (organizationId) params.organizationId = organizationId;

      const { data } = await axios.get<OAuthProvider[]>(
        `${config.backendApi}/api/auth/oauth/providers`,
        {
          params: Object.keys(params).length > 0 ? params : undefined,
          withCredentials: true,
        },
      );
      return data;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}
